import {
    Box,
    IconButton,
    Theme,
    Typography,
} from "@mui/material";
import CustomePieChart from "../../../dashboard/chart/CustomePieChart";
import CircleIcon from '@mui/icons-material/Circle';
import { PortfolioAllocationProps } from "./types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GetInvestorAssetAllocationResponseBody } from "../../../../redux-store/types/analytics";
import { RootStateType } from "../../../../redux-store/reducers";
import { getInvestorAssetAllocation } from "../../../../redux-store/actions/analytics";
import { ErrorText } from "../../../dashboard/chart/CustomeLineChart";
import { calculateOthersInPieChart } from "../../../../utils/utilityFunctions";
import { PortfolioAllocationLoading } from "../../loadingComponents";
import { BlueRightIcon } from "../../../../assets/SvgIcons";
import ListModel from "../../../dashboard/chart/ListModel";
import { NO_DATA_AVAILABLE } from "../../../../utils/constant";

const pieChartColor = ["#00A76F", "#FFAB00", "#3C92DC ", "#B8E8FC", "#FF563080", "#0D3C6B"]


export default function PortfolioAllocation({
    clientCode,
    portfolioId
}: PortfolioAllocationProps): JSX.Element {
    const dispatch = useDispatch();

    const [isLoading, setLoading] = useState<boolean>(false)
    const [chartData, setChartData] = useState<GetInvestorAssetAllocationResponseBody>({} as GetInvestorAssetAllocationResponseBody)
    const [isModalOpen, setModalOpen] = useState<boolean>(false)

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);

    const fetchData = async (clientCode: string, portfolioId: string) => {
        try {
            setLoading(true)

            const payload = {
                clientCode,
                category: "Asset Group",
                rmId: `${iwappRmId}`,
                portfolio: portfolioId
            }

            const response = await (dispatch(getInvestorAssetAllocation(payload)) as unknown as Promise<GetInvestorAssetAllocationResponseBody>);

            setChartData(response)

        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData(clientCode, portfolioId)
    }, [clientCode, portfolioId])


    if (isLoading) {
        return <PortfolioAllocationLoading />
    }

    if (!isLoading && !chartData?.isSuccess) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: "20px"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: '25px',
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        Portfolio Allocation
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            lineHeight: "22.5px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            width: "100%"
                        })}
                    >
                        {NO_DATA_AVAILABLE}
                    </Typography>
                </Box>
            </Box>
        )
    }

    const modifiedData = calculateOthersInPieChart({
        labelData: chartData?.result?.labelData ?? [],
        totalValue: chartData?.result?.totalValue ?? {},
        data: chartData?.result?.data ?? []
    })

    return (
        <>
            <Box sx={{ height: "100%" }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineheight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Portfolio Allocation
                </Typography>
                {(
                    (chartData?.result?.data ?? []).length === 0 ||
                    (chartData?.result?.labelData ?? []).length === 0 ||
                    Object.keys(chartData?.result?.totalValue ?? {})?.length === 0
                ) ?
                    <ErrorText /> :
                    <CustomePieChart
                        data={modifiedData?.data ?? []}
                        isLoading={false}
                        labelData={modifiedData?.labelData ?? []}
                        totalValue={modifiedData?.totalValue ?? {}}
                        showSlider={false}
                        align='center'
                    />
                }
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "25px"
                    }}
                >
                    {((chartData?.result?.labelData ?? []).length < 6) ? (
                        (chartData?.result?.labelData ?? []).map((obj: { label: string, value: number }, index: number) => (
                            <Box
                                key={`${obj?.label ?? ""}-${index}`}
                                sx={{
                                    display: "flex",
                                    columnGap: "15px",
                                    alignItems: 'center'
                                }}
                            >
                                <Box>
                                    <CircleIcon
                                        style={{
                                            color: pieChartColor[index],
                                            width: '11px',
                                            height: '11px',
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center"
                                    }}
                                >
                                    <Typography
                                        variant="subtitle_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {obj.label}
                                    </Typography>
                                    <Typography
                                        variant="subtitle_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {!Number.isNaN(Number(obj.value ?? 0)) ?
                                            `${Math.round((Number(obj.value ?? 0)) * 100) / 100}%` : '0%'}
                                    </Typography>
                                </Box>
                            </Box>

                        ))
                    ) : (
                        (modifiedData?.labelData ?? []).slice(0, 6).map((obj: { label: string, value: number }, index: number) => (
                            <Box
                                key={`${obj?.label ?? ""}-${index}`}
                                sx={{
                                    display: "flex",
                                    columnGap: "15px",
                                    alignItems: 'center'
                                }}
                            >
                                <Box>
                                    <CircleIcon
                                        style={{
                                            color: 5 === index ? "transparent" : pieChartColor[index],
                                            width: '11px',
                                            height: '11px',
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center"
                                    }}
                                >
                                    <Typography
                                        variant="subtitle_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {index === 5 ? 'Show More' : (obj?.label ?? "")}
                                    </Typography>
                                    <Typography
                                        variant="subtitle_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {index === 5 ? (
                                            <IconButton onClick={() => setModalOpen(true)}>
                                                <BlueRightIcon />
                                            </IconButton>
                                        ) : (
                                            !Number.isNaN(Number(obj.value ?? 0)) ?
                                                `${Math.round((Number(obj.value ?? 0)) * 100) / 100}%` : '0%'
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box >
            </Box >
            <ListModel
                open={isModalOpen}
                handleClose={() => setModalOpen(false)}
                labelData={(chartData?.result?.labelData ?? [])}
                title="Portfolio Allocation"
            />
        </>
    )
}